import { configureStore as configureReduxStore, Tuple } from "@reduxjs/toolkit";
import type { Store } from "redux";
import { combineReducers } from "redux";
import type { IAccountModel } from "./account/account-reducer";
import { accountReducer } from "./account/account-reducer";
import type { IEarningsModel } from "./account/earnings/earnings-reducer";
import { earningsReducer } from "./account/earnings/earnings-reducer";
import type { IBackgroundUploaderModel } from "./background-uploader/background-uploader-reducer";
import { backgroundUploaderReducer } from "./background-uploader/background-uploader-reducer";
import type { IChatsModel } from "./chats/chats-reducer";
import { chatsReducer } from "./chats/chats-reducer";
import type { IMessengerModel } from "./chats/messenger/messenger-reducer";
import { messengerReducer } from "./chats/messenger/messenger-reducer";
import type { IHeaderModalModel } from "./common/header-modal/header-modal-reducer";
import { headerModalReducer } from "./common/header-modal/header-modal-reducer";
import type { IDiscoverModel } from "./discover/discover-reducer";
import { discoverReducer } from "./discover/discover-reducer";
import type { IModelProfileModel } from "./discover/model-profile/model-profile-reducer";
import { modelProfileReducer } from "./discover/model-profile/model-profile-reducer";
import type { IFeedModel } from "./feed/feed-reducer";
import { feedReducer } from "./feed/feed-reducer";
// import { offline } from 'redux-offline';
// import offlineConfig from 'redux-offline/lib/defaults';
// import { composeWithDevTools } from 'redux-devtools-extension';
import type { ILandingModel } from "./landing/landing-reducer";
import { landingReducer } from "./landing/landing-reducer";
import type { IPopupModel } from "./popup/popup-reducer";
import { popupReducer } from "./popup/popup-reducer";
import type { IPromoModel } from "./promo/promo-reducer";
import { promoReducer } from "./promo/promo-reducer";
import type { IPubnubModel } from "./pubnub/pubnub-reducer";
import { pubnubReducer } from "./pubnub/pubnub-reducer";
import type { IWebsocketModel } from "./websocket/websocket-reducer";
import { websocketReducer } from "./websocket/websocket-reducer";

// Dynamically imported (not a static top-level import) so redux-logger's
// code is excluded from the production bundle entirely, matching the old
// CRA/webpack build's inline require() - a static import would get bundled
// even though it's never called, since Rollup doesn't eliminate this
// runtime-only conditional.
const devLogger = import.meta.env.DEV
  ? (await import("redux-logger")).createLogger
  : undefined;

export interface RootState {
  landingStore: ILandingModel;
  discoverStore: IDiscoverModel;
  promoStore: IPromoModel;
  accountStore: IAccountModel;
  earningsStore: IEarningsModel;
  feedStore: IFeedModel;
  chatsStore: IChatsModel;
  messengerStore: IMessengerModel;
  modelProfileStore: IModelProfileModel;
  headerModalStore: IHeaderModalModel;
  pubnubStore: IPubnubModel;
  popupStore: IPopupModel;
  backgroundUploaderStore: IBackgroundUploaderModel;
  websocketStore: IWebsocketModel;
}

export function configureStore(initialState?: RootState): Store<RootState> {
  const rootReducer = combineReducers({
    landingStore: landingReducer,
    discoverStore: discoverReducer,
    promoStore: promoReducer,
    accountStore: accountReducer,
    earningsStore: earningsReducer,
    feedStore: feedReducer,
    chatsStore: chatsReducer,
    messengerStore: messengerReducer,
    modelProfileStore: modelProfileReducer,
    headerModalStore: headerModalReducer,
    pubnubStore: pubnubReducer,
    popupStore: popupReducer,
    backgroundUploaderStore: backgroundUploaderReducer,
    websocketStore: websocketReducer,
  });

  const store = configureReduxStore({
    reducer: rootReducer as any,
    preloadedState: initialState,
    devTools: import.meta.env.DEV,
    middleware: (getDefaultMiddleware) => {
      const middleware = getDefaultMiddleware({
        actionCreatorCheck: false,
        immutableCheck: false,
        serializableCheck: false,
        thunk: false,
      });

      if (devLogger) {
        return new Tuple(devLogger({ collapsed: true }));
      }

      return middleware;
    },
  }) as Store<RootState>;

  // if (module.hot) {
  //   module.hot.accept('./reducer-root', () => {
  //     const nextReducer = require('./reducer-root');
  //     store.replaceReducer(nextReducer);
  //   });
  // }

  return store;
}
